// Questions about the files on quality records.
//
// THIS DOES NOT GO TO t2, unlike data/assistant.ts beside it. The ParkourAI Assistant is a tool-using
// agent in the Developer app, reached cross-app; this is a Foundation endpoint, and the difference is
// not plumbing. A read of a record in this app is scoped by the active persona, and t1's own door is
// the only one that VERIFIES the persona a request claims (handlers/cres/handler.py applies
// with_verified_persona; t2 has no persona model to verify against). Routing a document question
// through t2 would mean the visibility headers travelled somewhere nothing checks them.
//
// So: the FoundationAPI client, which attaches visibility-mode and visibility-value from the active
// persona, exactly as the attachment listing and download do.
//
//   POST t1/v1/attachment_questions {Question, Scope, ..., Messages} -> {Answer, Citations, ...}
//
// The conversation is stateless server-side and only the PROSE is round-tripped. The files are
// resolved from the scope again on every turn, so the history cannot be edited into a wider scope.
import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";

/** What the answer rested on. A page citation is what makes an answer about a controlled document
 *  checkable rather than merely plausible. */
export type AttachmentCitation = {
    /** The record and filename, as the server labelled it. */
    document: string;
    page: number | null;
    throughPage: number | null;
    quote: string;
};

/** A file that was read, or one that was not and why. */
export type ReadFile = {name: string; label?: string; mediaType?: string; bytes?: number};
export type UnreadFile = {name: string; label?: string; why: string};

export type AskedRecord = {usid: string; label: string | null};

export type AttachmentAnswer = {
    answer: string;
    citations: AttachmentCitation[];
    model: string | null;
    /** The records the answer is actually about — not necessarily the ones asked for. */
    records: AskedRecord[];
    read: ReadFile[];
    unreadable: UnreadFile[];
    recordsAsked: number;
    recordsVisible: number;
    /** Asked for but not visible to this persona. Shown, never swallowed. */
    recordsNotVisible: number;
};

/** One turn of the conversation, prose only. */
export type AttachmentTurn = {role: "user" | "assistant"; text: string};

export type AttachmentScope =
    | {kind: "Attachment"; attachmentUSID: string; label?: string}
    | {kind: "Revision"; objectRevisionUSID: string; label?: string}
    | {kind: "Records"; objectRevisionUSIDs: string[]; label?: string};

function payloadFor(scope: AttachmentScope) {
    if (scope.kind === "Attachment") return {Scope: "Attachment", AttachmentUSID: scope.attachmentUSID};
    if (scope.kind === "Revision") return {Scope: "Revision", ObjectRevisionUSID: scope.objectRevisionUSID};
    return {Scope: "Records", ObjectRevisionUSIDs: scope.objectRevisionUSIDs};
}

export async function askAboutAttachments(
    sessionContext: any, question: string, scope: AttachmentScope, priorTurns: AttachmentTurn[],
): Promise<AttachmentAnswer> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const res = await api({
        method: "POST",
        url: "t1/v1/attachment_questions",
        data: {
            Question: question,
            ...payloadFor(scope),
            // Prose only, and the server rebuilds the documents from the scope.
            Messages: priorTurns.map(turn => ({role: turn.role, text: turn.text})),
        },
    });
    const body = res?.data ?? {};
    return {
        answer: String(body.Answer ?? ""),
        citations: (Array.isArray(body.Citations) ? body.Citations : []).map((one: any) => ({
            document: String(one?.Document ?? ""),
            page: typeof one?.Page === "number" ? one.Page : null,
            throughPage: typeof one?.ThroughPage === "number" ? one.ThroughPage : null,
            quote: String(one?.Quote ?? ""),
        })),
        model: body.Model ? String(body.Model) : null,
        records: (Array.isArray(body.Records) ? body.Records : []).map((one: any) => ({
            usid: String(one?.ObjectRevisionUSID ?? ""),
            label: one?.Label ? String(one.Label) : null,
        })),
        read: (Array.isArray(body.Read) ? body.Read : []).map((one: any) => ({
            name: String(one?.Name ?? ""), label: one?.Label ? String(one.Label) : undefined,
            mediaType: one?.MediaType ? String(one.MediaType) : undefined,
            bytes: typeof one?.Bytes === "number" ? one.Bytes : undefined,
        })),
        unreadable: (Array.isArray(body.Unreadable) ? body.Unreadable : []).map((one: any) => ({
            name: String(one?.Name ?? ""), label: one?.Label ? String(one.Label) : undefined,
            why: String(one?.Why ?? ""),
        })),
        recordsAsked: Number(body.RecordsAsked ?? 0),
        recordsVisible: Number(body.RecordsVisible ?? 0),
        recordsNotVisible: Number(body.RecordsNotVisible ?? 0),
    };
}
